/**
 * Miss Salsa Mega Menu
 * Vanilla JavaScript - No dependencies
 * Handles desktop dropdowns, mobile menu, announcement bar, and search overlay
 */

(function() {
  'use strict';

  // ============================================================================
  // Configuration
  // ============================================================================

  const CONFIG = {
    hoverDelay: 150,        // Delay before opening dropdown on hover
    closeDelay: 300,        // Delay before closing dropdown on mouse leave
    mobileBreakpoint: 1024, // Breakpoint for mobile/desktop switch
    storageKey: 'ms-announcement-dismissed',
    transitionDuration: 300 // For panel animations
  };

  // ============================================================================
  // State Management
  // ============================================================================

  const state = {
    activeDropdown: null,
    hoverTimeout: null,
    closeTimeout: null,
    isMobileOpen: false,
    activePanel: 'main',
    isDesktop: window.innerWidth >= CONFIG.mobileBreakpoint,
    scrollHandler: null
  };

  // ============================================================================
  // Initialization
  // ============================================================================

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initDesktopDropdowns();
    initMobileMenu();
    initAnnouncement();
    initSearch();
    initKeyboard();
    initClickOutside();
    initResize();
    initDropdownNavigation();

    console.log('Miss Salsa Mega Menu initialized');
  }

  // ============================================================================
  // Dropdown Navigation Handler - Close dropdown before navigating
  // ============================================================================

  function initDropdownNavigation() {
    // Find all links inside megamenu dropdowns (not the parent nav links)
    const dropdownLinks = document.querySelectorAll('.megamenu__dropdown a');

    dropdownLinks.forEach(link => {
      link.addEventListener('click', handleDropdownLinkClick);
    });

    // Also close dropdowns on page show (handles bfcache)
    window.addEventListener('pageshow', forceCloseAllDropdowns);

    // Close any open dropdowns that might persist from previous page
    forceCloseAllDropdowns();
  }

  function handleDropdownLinkClick(e) {
    console.log('[Megamenu] Link clicked, closing dropdown');

    // Get the target URL before we do anything
    const targetUrl = e.currentTarget.href;

    // Prevent the default navigation temporarily
    e.preventDefault();

    // Close all dropdowns immediately
    forceCloseAllDropdowns();

    // Navigate after a brief delay to ensure dropdown is closed
    // This prevents visual glitches during page transition
    setTimeout(() => {
      window.location.href = targetUrl;
    }, 50);
  }

  function forceCloseAllDropdowns() {
    // Close all dropdowns instantly (no animation)
    const allItems = document.querySelectorAll('.megamenu__nav-item--has-mega');
    allItems.forEach(item => {
      item.classList.remove('is-open');
      const link = item.querySelector('.megamenu__nav-link');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
      }
      // Also explicitly hide the dropdown element
      const dropdown = item.querySelector('.megamenu__dropdown');
      if (dropdown) {
        dropdown.style.opacity = '0';
        dropdown.style.visibility = 'hidden';
        dropdown.style.pointerEvents = 'none';
      }
    });

    // Reset state
    state.activeDropdown = null;

    // Remove scroll listener
    if (state.scrollHandler) {
      window.removeEventListener('scroll', state.scrollHandler);
      state.scrollHandler = null;
    }

    // Force the header to remain visible
    const megamenuHeader = document.querySelector('.megamenu__header');
    const megamenu = document.querySelector('.megamenu');
    if (megamenuHeader) {
      megamenuHeader.style.cssText = 'visibility: visible !important; opacity: 1 !important;';
    }
    if (megamenu) {
      megamenu.style.cssText = 'visibility: visible !important; opacity: 1 !important;';
    }
  }

  // ============================================================================
  // Desktop Dropdown Functions
  // ============================================================================

  function initDesktopDropdowns() {
    const items = document.querySelectorAll('.megamenu__nav-item--has-mega');

    items.forEach(item => {
      // Mouse events for hover interaction
      item.addEventListener('mouseenter', handleMouseEnter);
      item.addEventListener('mouseleave', handleMouseLeave);

      // Keyboard support - focus/blur
      const link = item.querySelector('.megamenu__nav-link');
      if (link) {
        link.addEventListener('focus', () => openDropdown(item));
        link.addEventListener('blur', handleLinkBlur);
      }

      // Prevent default click on parent links with dropdowns
      if (link) {
        link.addEventListener('click', (e) => {
          if (state.isDesktop && item.classList.contains('megamenu__nav-item--has-mega')) {
            e.preventDefault();
            toggleDropdown(item);
          }
        });
      }
    });
  }

  function handleMouseEnter(e) {
    const item = e.currentTarget;

    // Only handle hover on desktop
    if (!state.isDesktop) return;

    // Clear any pending close timeout
    clearTimeout(state.closeTimeout);

    // Set hover timeout to open dropdown
    state.hoverTimeout = setTimeout(() => {
      openDropdown(item);
    }, CONFIG.hoverDelay);
  }

  function handleMouseLeave(e) {
    const item = e.currentTarget;

    // Only handle hover on desktop
    if (!state.isDesktop) return;

    // Clear hover timeout
    clearTimeout(state.hoverTimeout);

    // Set close timeout
    state.closeTimeout = setTimeout(() => {
      closeAllDropdowns();
    }, CONFIG.closeDelay);
  }

  function handleLinkBlur(e) {
    // Small delay to check if focus moved to dropdown content
    setTimeout(() => {
      if (!document.activeElement.closest('.megamenu__nav-item--has-mega')) {
        closeAllDropdowns();
      }
    }, 100);
  }

  function toggleDropdown(item) {
    if (item.classList.contains('is-open')) {
      closeAllDropdowns();
    } else {
      openDropdown(item);
    }
  }

  function openDropdown(item) {
    // Close any currently open dropdown first
    if (state.activeDropdown && state.activeDropdown !== item) {
      closeDropdown(state.activeDropdown);
    }

    // Open the new dropdown
    item.classList.add('is-open');

    const link = item.querySelector('.megamenu__nav-link');
    if (link) {
      link.setAttribute('aria-expanded', 'true');
    }

    // Position the dropdown below the header (for position: fixed)
    const dropdown = item.querySelector('.megamenu__dropdown');
    if (dropdown) {
      // Clear any inline styles set by forceCloseAllDropdowns
      dropdown.style.opacity = '';
      dropdown.style.visibility = '';
      dropdown.style.pointerEvents = '';

      updateDropdownPosition(dropdown);

      // Add scroll listener to update position
      state.scrollHandler = () => updateDropdownPosition(dropdown);
      window.addEventListener('scroll', state.scrollHandler, { passive: true });
    }

    state.activeDropdown = item;
  }

  function updateDropdownPosition(dropdown) {
    const header = document.querySelector('.megamenu__header');
    if (header && dropdown) {
      const headerRect = header.getBoundingClientRect();
      dropdown.style.top = headerRect.bottom + 'px';
    }
  }

  function closeDropdown(item) {
    item.classList.remove('is-open');

    const link = item.querySelector('.megamenu__nav-link');
    if (link) {
      link.setAttribute('aria-expanded', 'false');
    }

    // Remove scroll listener
    if (state.scrollHandler) {
      window.removeEventListener('scroll', state.scrollHandler);
      state.scrollHandler = null;
    }
  }

  function closeAllDropdowns() {
    const items = document.querySelectorAll('.megamenu__nav-item--has-mega');
    items.forEach(item => {
      item.classList.remove('is-open');
      const link = item.querySelector('.megamenu__nav-link');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
      }
    });

    // Remove scroll listener
    if (state.scrollHandler) {
      window.removeEventListener('scroll', state.scrollHandler);
      state.scrollHandler = null;
    }

    state.activeDropdown = null;
  }

  // ============================================================================
  // Mobile Menu Functions
  // ============================================================================

  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-toggle]');
    const close = document.querySelector('[data-mobile-close]');
    const overlay = document.querySelector('[data-mobile-overlay]');

    // Event listeners for open/close
    if (toggle) {
      toggle.addEventListener('click', function(e) {
        e.preventDefault();
        openMobile();
      });
    }

    if (close) {
      close.addEventListener('click', closeMobile);
    }

    if (overlay) {
      overlay.addEventListener('click', closeMobile);
    }

    // Panel navigation
    const panelButtons = document.querySelectorAll('[data-open-panel]');
    panelButtons.forEach(btn => {
      btn.addEventListener('click', handlePanelOpen);
    });

    const backButtons = document.querySelectorAll('[data-back-panel]');
    backButtons.forEach(btn => {
      btn.addEventListener('click', handlePanelBack);
    });
  }

  function openMobile() {
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-overlay]');
    const toggle = document.querySelector('[data-mobile-toggle]');

    if (menu) {
      menu.classList.add('is-open');
    }

    if (overlay) {
      overlay.classList.add('is-open');
    }

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
      toggle.classList.add('is-open');
    }

    // Lock body scroll
    document.body.classList.add('megamenu-mobile-open');

    state.isMobileOpen = true;
  }

  function closeMobile() {
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-overlay]');
    const toggle = document.querySelector('[data-mobile-toggle]');

    if (menu) {
      menu.classList.remove('is-open');
    }

    if (overlay) {
      overlay.classList.remove('is-open');
    }

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.classList.remove('is-open'); // Reset hamburger icon
    }

    // Unlock body scroll
    document.body.classList.remove('megamenu-mobile-open');

    state.isMobileOpen = false;

    // Reset to main panel after animation
    setTimeout(() => {
      resetPanels();
    }, CONFIG.transitionDuration);
  }

  function handlePanelOpen(e) {
    e.preventDefault();
    const panelName = e.currentTarget.dataset.openPanel;
    if (panelName) {
      showPanel(panelName);
    }
  }

  function handlePanelBack(e) {
    e.preventDefault();
    showPanel('main');
  }

  function showPanel(panelName) {
    // Remove active class from all panels
    const allPanels = document.querySelectorAll('[data-panel]');
    allPanels.forEach(panel => {
      panel.classList.remove('is-active');
    });

    // Add active class to target panel
    const targetPanel = document.querySelector(`[data-panel="${panelName}"]`);
    if (targetPanel) {
      targetPanel.classList.add('is-active');
    }

    state.activePanel = panelName;
  }

  function resetPanels() {
    showPanel('main');
  }

  // ============================================================================
  // Announcement Bar
  // ============================================================================

  function initAnnouncement() {
    const announcement = document.querySelector('[data-announcement]');
    const closeBtn = document.querySelector('[data-announcement-close]');

    if (!announcement || !closeBtn) return;

    // Check if announcement was already dismissed this session
    if (sessionStorage.getItem(CONFIG.storageKey)) {
      announcement.style.display = 'none';
      return;
    }

    // Close button handler
    closeBtn.addEventListener('click', () => {
      announcement.style.display = 'none';
      sessionStorage.setItem(CONFIG.storageKey, 'true');
    });
  }

  // ============================================================================
  // Search Overlay with Predictive Search
  // ============================================================================

  let searchDebounceTimer = null;

  function initSearch() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchOverlay = document.querySelector('[data-search-overlay]');
    const searchClose = document.querySelector('[data-search-close]');
    const searchInput = document.querySelector('[data-predictive-search-input]');

    if (!searchToggle || !searchOverlay) return;

    // Open search overlay
    searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      openSearch(searchOverlay, searchInput);
    });

    // Close search overlay
    if (searchClose) {
      searchClose.addEventListener('click', () => {
        closeSearch(searchOverlay);
      });
    }

    // Close on backdrop click
    searchOverlay.addEventListener('click', (e) => {
      if (e.target === searchOverlay) {
        closeSearch(searchOverlay);
      }
    });

    // Predictive search on input
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();

        clearTimeout(searchDebounceTimer);

        if (query.length < 2) {
          hideSearchResults();
          return;
        }

        searchDebounceTimer = setTimeout(() => {
          performPredictiveSearch(query);
        }, 300);
      });
    }
  }

  function openSearch(overlay, input) {
    overlay.classList.add('is-open');
    document.body.classList.add('megamenu-search-open');

    // Reset search state
    hideSearchResults();

    // Focus input after animation
    setTimeout(() => {
      if (input) {
        input.focus();
      }
    }, 100);
  }

  function closeSearch(overlay) {
    overlay.classList.remove('is-open');
    document.body.classList.remove('megamenu-search-open');

    // Clear search input
    const input = document.querySelector('[data-predictive-search-input]');
    if (input) input.value = '';

    hideSearchResults();
  }

  function performPredictiveSearch(query) {
    const resultsContainer = document.querySelector('[data-predictive-search-results]');
    const productsEl = document.querySelector('[data-search-products]');
    const viewAllEl = document.querySelector('[data-search-view-all]');
    const noResultsEl = document.querySelector('[data-search-no-results]');
    const popularEl = document.querySelector('[data-search-popular]');

    if (!resultsContainer) return;

    // Show loading state, hide popular
    resultsContainer.classList.remove('has-results', 'no-results');
    resultsContainer.classList.add('is-loading');
    if (productsEl) productsEl.innerHTML = '';
    if (popularEl) popularEl.style.display = 'none';

    // Fetch predictive search results
    fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`)
      .then(response => response.json())
      .then(data => {
        resultsContainer.classList.remove('is-loading');

        const products = data.resources?.results?.products || [];

        if (products.length === 0) {
          resultsContainer.classList.add('no-results');
          if (noResultsEl) noResultsEl.textContent = 'Geen resultaten gevonden voor "' + query + '"';
          return;
        }

        resultsContainer.classList.add('has-results');

        // Render products
        if (productsEl) {
          productsEl.innerHTML = products.map(product => {
            const imageUrl = product.featured_image?.url
              ? product.featured_image.url.replace(/\?.*$/, '') + '?width=150&height=150&crop=center'
              : '';

            return `
              <a href="${product.url}" class="megamenu__search-product">
                ${imageUrl
                  ? `<img src="${imageUrl}" alt="${product.title}" class="megamenu__search-product-image" loading="lazy">`
                  : '<div class="megamenu__search-product-image"></div>'}
                <div class="megamenu__search-product-info">
                  <span class="megamenu__search-product-title">${product.title}</span>
                  <span class="megamenu__search-product-price">${formatMoney(product.price)}</span>
                </div>
              </a>
            `;
          }).join('');
        }

        // Update view all link
        if (viewAllEl) {
          viewAllEl.href = `/search?q=${encodeURIComponent(query)}`;
        }
      })
      .catch(error => {
        console.error('Predictive search error:', error);
        resultsContainer.classList.remove('is-loading');
        resultsContainer.classList.add('no-results');
        if (noResultsEl) noResultsEl.textContent = 'Er ging iets mis bij het zoeken';
      });
  }

  function hideSearchResults() {
    const resultsContainer = document.querySelector('[data-predictive-search-results]');
    const popularEl = document.querySelector('[data-search-popular]');
    const productsEl = document.querySelector('[data-search-products]');

    if (resultsContainer) {
      resultsContainer.classList.remove('is-loading', 'has-results', 'no-results');
    }
    if (popularEl) popularEl.style.display = 'block';
    if (productsEl) productsEl.innerHTML = '';
  }

  function formatMoney(price) {
    // Shopify JSON API returns price as string in euros (e.g., "47.50"), not cents
    const amount = parseFloat(price).toFixed(2).replace('.', ',');
    return `€${amount}`;
  }

  // ============================================================================
  // Keyboard Navigation
  // ============================================================================

  function initKeyboard() {
    document.addEventListener('keydown', handleKeydown);
  }

  function handleKeydown(e) {
    // Escape key - close everything
    if (e.key === 'Escape' || e.keyCode === 27) {
      closeAllDropdowns();

      const searchOverlay = document.querySelector('[data-search-overlay]');
      if (searchOverlay && searchOverlay.classList.contains('is-open')) {
        closeSearch(searchOverlay);
      }

      if (state.isMobileOpen) {
        closeMobile();
      }
    }

    // Arrow keys for dropdown navigation (optional enhancement)
    if (state.activeDropdown && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      handleArrowNavigation(e);
    }
  }

  function handleArrowNavigation(e) {
    const dropdown = state.activeDropdown.querySelector('.megamenu__dropdown');
    if (!dropdown) return;

    const focusableElements = dropdown.querySelectorAll('a, button');
    const focusedIndex = Array.from(focusableElements).indexOf(document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = focusedIndex + 1;
      if (nextIndex < focusableElements.length) {
        focusableElements[nextIndex].focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = focusedIndex - 1;
      if (prevIndex >= 0) {
        focusableElements[prevIndex].focus();
      }
    }
  }

  // ============================================================================
  // Click Outside Handler
  // ============================================================================

  function initClickOutside() {
    document.addEventListener('click', handleClickOutside);
  }

  function handleClickOutside(e) {
    // Only on desktop
    if (!state.isDesktop) return;

    // Check if click is outside menu area
    const megamenu = document.querySelector('[data-megamenu]');
    if (!megamenu) return;

    // If click is outside megamenu, close dropdowns
    if (!megamenu.contains(e.target)) {
      closeAllDropdowns();
    }
  }

  // ============================================================================
  // Resize Handler
  // ============================================================================

  function initResize() {
    let resizeTimeout;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    });
  }

  function handleResize() {
    const wasDesktop = state.isDesktop;
    state.isDesktop = window.innerWidth >= CONFIG.mobileBreakpoint;

    // If switching from mobile to desktop, close mobile menu
    if (!wasDesktop && state.isDesktop && state.isMobileOpen) {
      closeMobile();
    }

    // If switching from desktop to mobile, close dropdowns
    if (wasDesktop && !state.isDesktop) {
      closeAllDropdowns();
    }
  }

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Trap focus within an element (useful for modals/overlays)
   */
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  /**
   * Debounce function for performance
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // ============================================================================
  // Public API (if needed for external access)
  // ============================================================================

  window.MissSalsaMegaMenu = {
    openMobile: openMobile,
    closeMobile: closeMobile,
    closeAllDropdowns: closeAllDropdowns,
    state: state
  };

})();
